import { useAuth } from "@dl/web-core-js/auth";
import { cn, Input } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, PartGridClient, type PartGridDTO } from "../../api/manufacturing";

/**
 * Finds a part by number or name.
 *
 * <p>A part is one of 27,582, which is a search and not a dropdown: a native
 * select with that many options is unusable in every browser. This asks the
 * server for ten matches at a time from the paged grid endpoint, so the same
 * search the Parts screen runs is the one used here.</p>
 *
 * <p>Keyboard first: arrows move, Enter picks, Escape closes. The on-hand
 * figure is shown beside each match because the person picking a part for a
 * correction almost always wants to know where it stands.</p>
 */
export function PartPicker({
  onPick,
  placeholder,
  autoFocus,
  disabled = false,
  exclude = [],
}: {
  onPick: (part: PartGridDTO) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Parts already chosen, left out of the matches. */
  exclude?: number[];
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new PartGridClient(http), [http]);
  const listId = useId();

  const [text, setText] = useState("");
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const wrapper = useRef<HTMLDivElement>(null);

  // Debounced: a round trip per keystroke is wasted work on any link.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(text.trim()), 250);
    return () => clearTimeout(timer);
  }, [text]);

  const { data, isFetching } = useQuery({
    queryKey: ["manufacturing", "part-picker", term],
    queryFn: () => client.find({ page: 0, size: 10, q: term, sort: ["number,asc"] }),
    enabled: term.length >= 2,
    staleTime: 60_000,
  });

  const matches = (data?.content ?? []).filter((part) => !exclude.includes(part.id));

  useEffect(() => setCursor(0), [term, data]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (part: PartGridDTO) => {
    onPick(part);
    setText("");
    setTerm("");
    setOpen(false);
  };

  return (
    <div ref={wrapper} className="relative">
      <Input
        value={text}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={placeholder ?? t("part.picker.placeholder", "Search a part by number or name")}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            setCursor((index) => Math.min(index + 1, Math.max(0, matches.length - 1)));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setCursor((index) => Math.max(index - 1, 0));
          } else if (event.key === "Enter") {
            // Only from the list on screen, and only once it answers what is
            // typed: Enter inside the debounce would pick a match for the
            // shorter text.
            const chosen = open && term === text.trim() ? matches[cursor] : undefined;
            if (chosen) {
              event.preventDefault();
              pick(chosen);
            }
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && !disabled && term.length >= 2 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-line bg-surface py-1 shadow-lg"
        >
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-[12px] text-ink-muted">
              {isFetching ? t("common.loading", "Loading…") : t("part.picker.none", "No part matches.")}
            </li>
          ) : (
            matches.map((part, index) => (
              <li
                key={part.id}
                role="option"
                aria-selected={index === cursor}
                onMouseEnter={() => setCursor(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(part);
                }}
                className={cn(
                  "flex cursor-pointer items-baseline gap-2 px-3 py-1.5 text-[13px]",
                  index === cursor ? "bg-accent-soft" : "",
                )}
              >
                <span className="font-medium text-ink">{part.number}</span>
                <span className="min-w-0 flex-1 truncate text-[12px] text-ink-muted">{part.name}</span>
                {part.status === "INACTIVE" && (
                  <span className="text-[11px] text-risk">{t("status.inactive", "Inactive")}</span>
                )}
                <span className="shrink-0 text-[11px] tabular-nums text-ink-faint">
                  {formatNumber(part.on_hand)} {part.stock_unit_code ?? ""}
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
